import { useState, type FormEvent } from "react";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Label from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SUBMIT_URL = "https://readdy.ai/api/form/daoi04us79n0eakeffq0";

const subjects = [
  "General enquiry",
  "Appointment question",
  "Spine care",
  "Orthopaedic care",
  "Reports & records",
  "Other",
];

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("Thank you for reaching out. Our team will get back to you shortly.");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "Something went wrong.";

      if (response.ok && code === "OK") {
        setStatus("success");
        setMessage("Thank you for reaching out. Our team will get back to you shortly.");
        form.reset();
      } else {
        setStatus("error");
        setMessage(String(serverMessage));
      }
    } catch {
      setStatus("error");
      setMessage("We could not send your message right now. Please call the clinic or try again.");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-accent-200 bg-accent-50 p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-background-50">
          <i className="ri-check-line text-2xl" aria-hidden="true"></i>
        </span>
        <h3 className="mt-5 font-heading text-xl font-semibold text-foreground-950">Message sent</h3>
        <p className="mx-auto mt-3 max-w-md font-body text-sm leading-relaxed text-foreground-700">{message}</p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 cursor-pointer rounded-md border border-accent-300 px-5 py-2.5 font-label text-sm font-semibold text-accent-800 transition-colors hover:bg-accent-100"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="contact_name">
            Your Name <span className="text-accent-600">*</span>
          </Label>
          <Input id="contact_name" name="name" type="text" required placeholder="Full name" />
        </div>

        <div>
          <Label htmlFor="contact_email">
            Email <span className="text-accent-600">*</span>
          </Label>
          <Input id="contact_email" name="email" type="email" required placeholder="you@example.com" />
        </div>

        <div>
          <Label htmlFor="contact_phone">Phone Number</Label>
          <Input id="contact_phone" name="phone" type="tel" placeholder="Mobile number" />
        </div>

        <div>
          <Label htmlFor="contact_subject">Subject</Label>
          <Select name="subject" defaultValue="">
            <SelectTrigger id="contact_subject">
              <SelectValue placeholder="Select a subject" />
            </SelectTrigger>
            <SelectContent>
              {subjects.map((subject) => (
                <SelectItem key={subject} value={subject}>
                  {subject}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="contact_message">
            Message <span className="text-accent-600">*</span>
          </Label>
          <Textarea
            id="contact_message"
            name="message"
            rows={5}
            required
            maxLength={500}
            placeholder="How can we help you?"
          />
          <p className="mt-1.5 font-body text-xs text-foreground-500">Maximum 500 characters.</p>
        </div>

        <input
          type="text"
          name="contact_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-honeypot-field"
        />
      </div>

      {status === "error" ? (
        <p
          role="alert"
          className="mt-5 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 font-body text-sm text-accent-900"
        >
          {message}
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-body text-xs leading-relaxed text-foreground-500">
          We usually respond within one working day. For urgent concerns, please call the clinic directly.
        </p>
        <Button type="submit" className="gap-2">
          <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
          Send Message
        </Button>
      </div>
    </form>
  );
}